import { FC, useState } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../../ActiveCareMapProvider';
import { EditNoteWithQueryParamDrawer } from '../edit-note/EditNoteWithQueryParamDrawer';
import { AppointmentEditorDrawer } from '../../appointments/AppointmentEditorDrawer';
import { NoteDetailBody } from './NoteDetailBody';
import { CommentFooter } from '../../comments/CommentFooter';
import { useFocusCommentOnce } from './useFocusCommentOnce';
import { CommentFocusRequests, ICommentFocusRequest } from './CommentFocusRequests';

interface IViewNoteDrawerProps {
    noteId: string;
    trackingFrom?: string;
    // open with the comment bar focused
    addComment?: ICommentFocusRequest;
};

export class ViewNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewNoteDrawerProps>){}

    mode = 'view-note';

    Component = ViewNoteDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewNoteDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };

    // The drawer over the notes page of a given care map, for links built away from that page.
    getUrlForCareMap = (careMapId: string, props: IViewNoteDrawerProps) => {
        return `/notes/${careMapId}?${this.drawerHelper.buildQueryParams({ mode: this.mode, ...props })}`;
    };
};

export const ViewNoteDrawerContent: FC<IViewNoteDrawerProps> = ({ noteId, trackingFrom }) => {
    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const appointmentEditorDrawer = useInstance(AppointmentEditorDrawer);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);

    return (
        <NoteDetailBody
            noteId={noteId}
            trackingFrom={trackingFrom}
            editHandler={() => editNoteDrawer.open({ noteId, trackingFrom })}
            onAppointmentClick={(appointment) => appointmentEditorDrawer.open({
                appointmentId: appointment.appointmentId,
                trackingFrom: 'care recipient'
            })}
            onCloseDrawer={drawerHelper.close}
            authenticatedRedirect={viewNoteDrawer.getUrl({ noteId, trackingFrom })}
        />
    );
};

export const ViewNoteDrawerView: FC<IViewNoteDrawerProps> = ({ noteId, trackingFrom, addComment }) => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);

    const commentFocusRequests = useInstance(CommentFocusRequests);
    const focusComment = useFocusCommentOnce(addComment || undefined);
    // for a reader asked to sign in before commenting, who returns to this note ready to type
    const [signInCommentRequest] = useState(() => commentFocusRequests.create());

    return (
        <QueryParamDrivenDrawerPage
            title="View Note"
            hasBack={false}
            data-type="view-note-drawer"
            onClose={drawerHelper.close}
            exteriorFooter={
                <BlockUntilActiveCaremap>
                    <CommentFooter
                        noteId={noteId}
                        autoFocus={focusComment}
                        surface="note_detail"
                        authenticatedRedirect={viewNoteDrawer.getUrl({ noteId, trackingFrom, addComment: signInCommentRequest })}
                        onNoteGone={drawerHelper.close}
                    />
                </BlockUntilActiveCaremap>
            }
        >
            <BlockUntilActiveCaremap>
                <ViewNoteDrawerContent
                    noteId={noteId}
                    trackingFrom={drawerHelper.getDrawerParams().trackingFrom}
                />
            </BlockUntilActiveCaremap>
        </QueryParamDrivenDrawerPage>
    );
};
